/** Keep in sync with netlify/functions/lib/account-api-catalog.js */
export const API_GROUPS = [
  {
    "id": "discovery",
    "title": "Discovery",
    "summary": "List every route and fetch the OpenAPI spec. No API key required."
  },
  {
    "id": "sensors",
    "title": "Sensors",
    "summary": "Devices on your account and their identity metadata."
  },
  {
    "id": "metrics",
    "title": "Metrics",
    "summary": "Temperature, humidity, battery, GPS and latest readings."
  },
  {
    "id": "zones",
    "title": "Zones",
    "summary": "Named locations that group sensors and gateways."
  },
  {
    "id": "gateways",
    "title": "Gateways",
    "summary": "KGateway and other gateways on your account."
  },
  {
    "id": "shipments",
    "title": "Shipments",
    "summary": "GS1 / EPCIS shipments, identity and status."
  },
  {
    "id": "alerts",
    "title": "Alerts",
    "summary": "Device alert rules and thresholds."
  },
  {
    "id": "reports",
    "title": "Reports",
    "summary": "Verification reports and public QR links."
  }
];

export const API_ROUTES = [
  {
    "id": "catalog",
    "group": "discovery",
    "method": "GET",
    "path": "/v1",
    "auth": false,
    "summary": "List every available API route",
    "description": "Returns the route catalog: groups, methods, paths, query parameters and example responses. Use this to discover endpoints from a client without hard-coding paths.",
    "curl": "curl https://YOUR_HOST/v1"
  },
  {
    "id": "openapi",
    "group": "discovery",
    "method": "GET",
    "path": "/v1/openapi.json",
    "auth": false,
    "summary": "OpenAPI 3 specification",
    "description": "Machine-readable OpenAPI 3 document for this API. Import into Postman, Insomnia or any OpenAPI client.",
    "curl": "curl https://YOUR_HOST/v1/openapi.json"
  },
  {
    "id": "list-sensors",
    "group": "sensors",
    "method": "GET",
    "path": "/v1/sensors",
    "auth": true,
    "summary": "List sensors",
    "description": "All sensors owned by the API key account.",
    "query": [
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–200). Default 100."
      },
      {
        "name": "offset",
        "type": "integer",
        "description": "Skip rows. Default 0."
      }
    ],
    "metrics": [
      "identity"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/sensors",
    "example": {
      "sensors": [
        {
          "id": "uuid",
          "name": "Cool room 1",
          "device_type": "s6",
          "zone_id": "uuid"
        }
      ],
      "count": 1
    }
  },
  {
    "id": "get-sensor",
    "group": "sensors",
    "method": "GET",
    "path": "/v1/sensors/{id}",
    "auth": true,
    "summary": "Get one sensor",
    "description": "Single sensor by id. Returns 404 if it is not on this account.",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Sensor id"
      }
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/sensors/{id}"
  },
  {
    "id": "sensor-latest",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/sensors/{id}/latest",
    "auth": true,
    "summary": "Latest reading for a sensor",
    "description": "Most recent temperature, humidity, battery and timestamp for one sensor.",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Sensor id"
      }
    ],
    "metrics": [
      "temperature",
      "humidity",
      "battery"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/sensors/{id}/latest",
    "example": {
      "sensor_id": "uuid",
      "temperature": 4.2,
      "humidity": 67.1,
      "battery_level": 91,
      "recorded_at": "2026-10-06T14:02:11.000Z"
    }
  },
  {
    "id": "sensor-readings",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/sensors/{id}/readings",
    "auth": true,
    "summary": "Historical readings for a sensor",
    "description": "Time series of temperature, humidity and battery. Filter with from/to (ISO-8601). Newest first.",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Sensor id"
      }
    ],
    "query": [
      {
        "name": "from",
        "type": "datetime",
        "description": "Inclusive start (ISO-8601)."
      },
      {
        "name": "to",
        "type": "datetime",
        "description": "Inclusive end (ISO-8601)."
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–1000). Default 100."
      },
      {
        "name": "offset",
        "type": "integer",
        "description": "Skip rows. Default 0."
      }
    ],
    "metrics": [
      "temperature",
      "humidity",
      "battery"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" \"https://YOUR_HOST/v1/sensors/{id}/readings?from=2026-10-01T00:00:00Z&limit=200\""
  },
  {
    "id": "metrics-latest",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/metrics/latest",
    "auth": true,
    "summary": "Latest metrics for every sensor",
    "description": "One latest reading per sensor on the account: temperature, humidity, battery.",
    "metrics": [
      "temperature",
      "humidity",
      "battery"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/metrics/latest"
  },
  {
    "id": "metrics-temperature",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/metrics/temperature",
    "auth": true,
    "summary": "Temperature time series",
    "description": "Temperature samples across one sensor or all sensors. Use sensor_id to scope.",
    "query": [
      {
        "name": "sensor_id",
        "type": "uuid",
        "description": "Optional. Limit to one sensor."
      },
      {
        "name": "from",
        "type": "datetime",
        "description": "Inclusive start (ISO-8601)."
      },
      {
        "name": "to",
        "type": "datetime",
        "description": "Inclusive end (ISO-8601)."
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–1000). Default 100."
      }
    ],
    "metrics": [
      "temperature"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" \"https://YOUR_HOST/v1/metrics/temperature?sensor_id={id}&from=2026-10-01T00:00:00Z\""
  },
  {
    "id": "metrics-humidity",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/metrics/humidity",
    "auth": true,
    "summary": "Humidity time series",
    "description": "Relative humidity samples across one sensor or all sensors.",
    "query": [
      {
        "name": "sensor_id",
        "type": "uuid",
        "description": "Optional. Limit to one sensor."
      },
      {
        "name": "from",
        "type": "datetime",
        "description": "Inclusive start (ISO-8601)."
      },
      {
        "name": "to",
        "type": "datetime",
        "description": "Inclusive end (ISO-8601)."
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–1000). Default 100."
      }
    ],
    "metrics": [
      "humidity"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" \"https://YOUR_HOST/v1/metrics/humidity?sensor_id={id}\""
  },
  {
    "id": "metrics-battery",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/metrics/battery",
    "auth": true,
    "summary": "Battery time series",
    "description": "Battery level samples across one sensor or all sensors.",
    "query": [
      {
        "name": "sensor_id",
        "type": "uuid",
        "description": "Optional. Limit to one sensor."
      },
      {
        "name": "from",
        "type": "datetime",
        "description": "Inclusive start (ISO-8601)."
      },
      {
        "name": "to",
        "type": "datetime",
        "description": "Inclusive end (ISO-8601)."
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–1000). Default 100."
      }
    ],
    "metrics": [
      "battery"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" \"https://YOUR_HOST/v1/metrics/battery?sensor_id={id}\""
  },
  {
    "id": "metrics-gps",
    "group": "metrics",
    "method": "GET",
    "path": "/v1/metrics/gps",
    "auth": true,
    "summary": "GPS tracks",
    "description": "Latitude, longitude, speed and heading from gps_tracks, optionally with temperature and humidity recorded at the same point.",
    "query": [
      {
        "name": "sensor_id",
        "type": "uuid",
        "description": "Optional. Limit to one sensor."
      },
      {
        "name": "from",
        "type": "datetime",
        "description": "Inclusive start (ISO-8601)."
      },
      {
        "name": "to",
        "type": "datetime",
        "description": "Inclusive end (ISO-8601)."
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–1000). Default 100."
      }
    ],
    "metrics": [
      "gps",
      "temperature",
      "humidity",
      "battery"
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" \"https://YOUR_HOST/v1/metrics/gps?from=2026-10-01T00:00:00Z\""
  },
  {
    "id": "list-zones",
    "group": "zones",
    "method": "GET",
    "path": "/v1/zones",
    "auth": true,
    "summary": "List zones",
    "description": "Zones (fridges, rooms, vehicles) on this account.",
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/zones"
  },
  {
    "id": "get-zone",
    "group": "zones",
    "method": "GET",
    "path": "/v1/zones/{id}",
    "auth": true,
    "summary": "Get one zone",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Zone id"
      }
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/zones/{id}"
  },
  {
    "id": "list-gateways",
    "group": "gateways",
    "method": "GET",
    "path": "/v1/gateways",
    "auth": true,
    "summary": "List gateways",
    "description": "Gateways on this account. Device credentials are never returned.",
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/gateways"
  },
  {
    "id": "get-gateway",
    "group": "gateways",
    "method": "GET",
    "path": "/v1/gateways/{id}",
    "auth": true,
    "summary": "Get one gateway",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Gateway id"
      }
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/gateways/{id}"
  },
  {
    "id": "list-shipments",
    "group": "shipments",
    "method": "GET",
    "path": "/v1/shipments",
    "auth": true,
    "summary": "List shipments",
    "description": "GS1 / EPCIS shipments owned by this account.",
    "query": [
      {
        "name": "status",
        "type": "string",
        "description": "Optional. commissioned | in_transit | delivered | quarantined"
      },
      {
        "name": "limit",
        "type": "integer",
        "description": "Max rows (1–200). Default 100."
      }
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/shipments"
  },
  {
    "id": "get-shipment",
    "group": "shipments",
    "method": "GET",
    "path": "/v1/shipments/{id}",
    "auth": true,
    "summary": "Get one shipment",
    "pathParams": [
      {
        "name": "id",
        "type": "uuid",
        "description": "Shipment id"
      }
    ],
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/shipments/{id}"
  },
  {
    "id": "list-alerts",
    "group": "alerts",
    "method": "GET",
    "path": "/v1/alerts",
    "auth": true,
    "summary": "List alert rules",
    "description": "Device alert rules (temperature / humidity thresholds) on this account.",
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/alerts"
  },
  {
    "id": "list-reports",
    "group": "reports",
    "method": "GET",
    "path": "/v1/reports",
    "auth": true,
    "summary": "List verification reports",
    "description": "Verify reports, including public URL and QR where present.",
    "curl": "curl -H \"Authorization: Bearer cv_live_YOUR_KEY\" https://YOUR_HOST/v1/reports"
  }
];

export function apiHost() {
  if (typeof window === "undefined") return "https://chillverify.com";
  return window.location.origin;
}

export function curlFor(route, host = apiHost()) {
  return String(route.curl || "").split("https://YOUR_HOST").join(host);
}
